import { useEffect } from "react";

const ignoredParents = new Set(["SCRIPT", "STYLE", "CODE", "PRE", "NOSCRIPT"]);

export default function CountryLanguage({ language, translations }) {
  useEffect(() => {
    document.documentElement.lang = language;
    const entries = Object.entries(translations).sort(([a], [b]) => b.length - a.length);

    const translateValue = (value) => {
      const trimmed = value.replace(/\s+/g, " ").trim();
      if (!trimmed) return value;
      let translated = translations[trimmed];
      if (!translated) {
        translated = trimmed;
        for (const [source, target] of entries) {
          if (source.length >= 8 && translated.includes(source)) {
            translated = translated.split(source).join(target);
          }
        }
      }
      if (translated === trimmed) return value;
      return `${value.match(/^\s*/)?.[0] || ""}${translated}${value.match(/\s*$/)?.[0] || ""}`;
    };

    const localize = (root) => {
      if (!(root instanceof Node)) return;
      if (root.nodeType === Node.TEXT_NODE) {
        if (!ignoredParents.has(root.parentElement?.tagName)) {
          const nextValue = translateValue(root.nodeValue);
          if (nextValue !== root.nodeValue) root.nodeValue = nextValue;
        }
        return;
      }
      if (!(root instanceof Element)) return;
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
      let node;
      while ((node = walker.nextNode())) {
        if (!ignoredParents.has(node.parentElement?.tagName)) {
          const nextValue = translateValue(node.nodeValue);
          if (nextValue !== node.nodeValue) node.nodeValue = nextValue;
        }
      }
      for (const element of [root, ...root.querySelectorAll("[title], [aria-label], [placeholder], img[alt]")]) {
        for (const attribute of ["title", "aria-label", "placeholder", "alt"]) {
          if (element.hasAttribute?.(attribute)) {
            const currentValue = element.getAttribute(attribute);
            const nextValue = translateValue(currentValue);
            if (nextValue !== currentValue) element.setAttribute(attribute, nextValue);
          }
        }
      }
      const nextTitle = translateValue(document.title);
      if (nextTitle !== document.title) document.title = nextTitle;
    };

    localize(document.body);
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach(localize);
        if (mutation.type === "characterData") localize(mutation.target);
      }
      const nextTitle = translateValue(document.title);
      if (nextTitle !== document.title) document.title = nextTitle;
    });
    observer.observe(document.body, { childList: true, subtree: true, characterData: true });
    return () => observer.disconnect();
  }, [language, translations]);

  return null;
}
